/**
 * PLAT-11228 (audio-first): timeline filmstrip frames from the session's
 * locally-held upload while the pipeline's per-second thumbnails are still
 * being extracted (they come off the 270p rendition, i.e. at the very end).
 * Pure helper — a hidden <video> seeks through the object URL and each frame
 * is drawn to a small canvas and handed back as a blob URL, in the same
 * `{ frameNumber, url }` shape the frame-loader worker produces.
 */

export interface LocalFrameThumbnail {
  frameNumber: number;
  url: string;
}

const SEEK_TIMEOUT_MS = 2500;
const METADATA_TIMEOUT_MS = 8000;

function waitForEvent(
  target: HTMLVideoElement,
  event: 'loadedmetadata' | 'seeked',
  timeoutMs: number,
): Promise<boolean> {
  return new Promise(resolve => {
    const timer = setTimeout(() => finish(false), timeoutMs);
    const finish = (ok: boolean) => {
      clearTimeout(timer);
      target.removeEventListener(event, onEvent);
      target.removeEventListener('error', onError);
      resolve(ok);
    };
    const onEvent = () => finish(true);
    const onError = () => finish(false);
    target.addEventListener(event, onEvent, { once: true });
    target.addEventListener('error', onError, { once: true });
  });
}

/**
 * Extract one frame per requested frame number (1-based seconds, matching the
 * pipeline's 000001.jpg… files) scaled to `height`. Sequential seeks on a
 * single element; frames that fail or time out are skipped. Never rejects —
 * an undecodable file simply yields no frames and the caller stays on its
 * existing processing state.
 *
 * Ownership: every returned `url` is a blob URL minted here
 * (`URL.createObjectURL`) and is NOT tracked by this module. The caller owns
 * them and must `URL.revokeObjectURL(url)` when a frame is dropped or
 * replaced (e.g. once the pipeline thumbnails arrive), otherwise the decoded
 * JPEGs stay resident for the life of the document.
 */
export async function extractLocalFrameThumbnails(
  objectUrl: string,
  frameNumbers: number[],
  height: number,
  signal?: AbortSignal,
): Promise<LocalFrameThumbnail[]> {
  const results: LocalFrameThumbnail[] = [];
  if (!frameNumbers.length || signal?.aborted) return results;

  const video = document.createElement('video');
  video.preload = 'auto';
  video.muted = true;
  video.playsInline = true;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return results;

  const cleanup = () => {
    video.removeAttribute('src');
    video.load();
  };

  try {
    const metadata = waitForEvent(video, 'loadedmetadata', METADATA_TIMEOUT_MS);
    video.src = objectUrl;
    if (!(await metadata) || !video.videoWidth || !video.videoHeight) {
      return results;
    }
    const scale = height / video.videoHeight;
    canvas.width = Math.max(1, Math.round(video.videoWidth * scale));
    canvas.height = Math.max(1, Math.round(height));
    const duration = Number.isFinite(video.duration) ? video.duration : 0;

    for (const frameNumber of frameNumbers) {
      if (signal?.aborted) break;
      // Frame N covers second N-1..N; sample its midpoint.
      const target = Math.max(0, frameNumber - 0.5);
      if (duration && target >= duration) continue;
      const seeked = waitForEvent(video, 'seeked', SEEK_TIMEOUT_MS);
      video.currentTime = target;
      if (!(await seeked)) continue;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise<Blob | null>(resolve =>
        canvas.toBlob(resolve, 'image/jpeg', 0.7),
      );
      if (!blob) continue;
      results.push({ frameNumber, url: URL.createObjectURL(blob) });
    }
    return results;
  } catch {
    return results;
  } finally {
    cleanup();
  }
}
